import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const emptyBlog = {
  id: null,
  slug: '',
  title: '',
  excerpt: '',
  category: '',
  date: '',
  readTime: '',
  image: '',
  sectionsText: '',
};

const BlogsSettings = () => {
  const [blogs, setBlogs] = useState([]);
  const [selected, setSelected] = useState(emptyBlog);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadBlogs = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API_BASE_URL}/api/admin/blogs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data.blogs || [];
      setBlogs(
        list.map((b) => ({
          ...b,
          sectionsText: Array.isArray(b.sections)
            ? b.sections
                .map(
                  (s) =>
                    `${s.heading || ''}\n${Array.isArray(s.paragraphs) ? s.paragraphs.join('\n') : ''}`
                )
                .join('\n\n')
            : '',
        }))
      );
    } catch (err) {
      const message = err.response?.data?.message || 'تعذر تحميل المقالات.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
  }, []);

  const startNew = () => {
    setSelected(emptyBlog);
    setSuccess('');
    setError('');
  };

  const startEdit = (blog) => {
    setSelected({
      id: blog.id,
      slug: blog.slug || '',
      title: blog.title || '',
      excerpt: blog.excerpt || '',
      category: blog.category || '',
      date: blog.date || '',
      readTime: blog.readTime || '',
      image: blog.image || '',
      sectionsText: blog.sectionsText || '',
    });
    setSuccess('');
    setError('');
  };

  const handleChange = (field, value) => {
    setSelected((prev) => ({ ...prev, [field]: value }));
  };

  const handleUploadImage = async (file) => {
    if (!file) return;
    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(`${API_BASE_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      if (res.data?.url) {
        setSelected((prev) => ({ ...prev, image: res.data.url }));
        setSuccess('تم رفع صورة المقال بنجاح.');
      }
    } catch (err) {
      const message =
        err.response?.data?.message || 'فشل رفع الصورة. تأكد من أن الملف صورة أقل من 5MB.';
      setError(message);
    }
  };

  const parseSections = (sectionsText) => {
    if (!sectionsText) return [];
    const blocks = sectionsText
      .split(/\n\s*\n/)
      .map((b) => b.trim())
      .filter(Boolean);
    return blocks
      .map((block) => {
        const lines = block
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean);
        if (!lines.length) return null;
        const [heading, ...paragraphs] = lines;
        return {
          heading,
          paragraphs,
        };
      })
      .filter(Boolean);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      setSaving(false);
      return;
    }

    const sections = parseSections(selected.sectionsText);

    const payload = {
      slug: selected.slug,
      title: selected.title,
      excerpt: selected.excerpt,
      category: selected.category,
      date: selected.date,
      readTime: selected.readTime,
      image: selected.image,
      sections,
    };

    try {
      if (selected.id) {
        await axios.put(`${API_BASE_URL}/api/admin/blogs/${selected.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('تم تحديث المقال بنجاح.');
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/blogs`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('تم إنشاء مقال جديد بنجاح.');
      }
      await loadBlogs();
      if (!selected.id) {
        setSelected(emptyBlog);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'حدث خطأ أثناء حفظ المقال.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المقال؟')) return;
    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError('انتهت صلاحية الجلسة، برجاء تسجيل الدخول مرة أخرى.');
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('تم حذف المقال بنجاح.');
      await loadBlogs();
      if (selected.id === id) {
        setSelected(emptyBlog);
      }
    } catch (err) {
      const message = err.response?.data?.message || 'فشل حذف المقال.';
      setError(message);
    }
  };

  if (loading) {
    return <p>جاري تحميل المقالات...</p>;
  }

  return (
    <div className="admin-section">
      <h2 className="admin-section-title">إدارة المدونة</h2>
      <p className="admin-section-subtitle">
        يمكنك هنا إضافة وتعديل وحذف مقالات المدونة التي تظهر في صفحة المدونة وتفاصيل المقال.
      </p>

      {error && <div className="admin-section-error">{error}</div>}
      {success && <div className="admin-section-success">{success}</div>}

      <div className="admin-grid-2">
        <div>
          <div className="admin-links-header">
            <h3>قائمة المقالات</h3>
            <button type="button" className="admin-btn-secondary" onClick={startNew}>
              مقال جديد
            </button>
          </div>
          <div className="admin-links-list">
            {blogs.map((b) => (
              <div key={b.id} className="admin-link-row">
                <div className="admin-link-fields">
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={() => startEdit(b)}
                  >
                    #{b.id} - {b.title}
                  </button>
                </div>
                <button
                  type="button"
                  className="admin-btn-danger"
                  onClick={() => handleDelete(b.id)}
                >
                  حذف
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 style={{ marginBottom: '0.75rem' }}>
            {selected.id ? `تعديل المقال رقم ${selected.id}` : 'إضافة مقال جديد'}
          </h3>

          <div className="admin-field">
            <label>Slug (المسار الفريد للمقال)</label>
            <input
              type="text"
              value={selected.slug}
              onChange={(e) => handleChange('slug', e.target.value)}
              placeholder="مثال: sfda-product-registration-basics"
            />
          </div>

          <div className="admin-field">
            <label>عنوان المقال</label>
            <input
              type="text"
              value={selected.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
          </div>

          <div className="admin-field">
            <label>الملخص (يظهر في بطاقة المقال)</label>
            <textarea
              rows={2}
              value={selected.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label>التصنيف</label>
              <input
                type="text"
                value={selected.category}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="مثال: هيئة الغذاء والدواء"
              />
            </div>
            <div className="admin-field">
              <label>تاريخ النشر</label>
              <input
                type="date"
                value={selected.date}
                onChange={(e) => handleChange('date', e.target.value)}
              />
            </div>
          </div>

          <div className="admin-grid-2">
            <div className="admin-field">
              <label>مدة القراءة (مثال: 7 دقائق)</label>
              <input
                type="text"
                value={selected.readTime}
                onChange={(e) => handleChange('readTime', e.target.value)}
                placeholder="7 دقائق"
              />
            </div>
            <div className="admin-field">
              <label>رابط صورة المقال</label>
              <input
                type="text"
                value={selected.image}
                onChange={(e) => handleChange('image', e.target.value)}
                placeholder="/uploads/blog-1.webp أو رابط خارجي"
              />
              <small>يمكنك رفع صورة أو استخدام رابط خارجي.</small>
              <div style={{ marginTop: '0.5rem' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleUploadImage(e.target.files?.[0] || null)}
                />
              </div>
            </div>
          </div>

          <div className="admin-field">
            <label>محتوى المقال (كل قسم: العنوان في السطر الأول، ثم الفقرات، وبين الأقسام سطر فارغ)</label>
            <textarea
              rows={10}
              value={selected.sectionsText}
              onChange={(e) => handleChange('sectionsText', e.target.value)}
              style={{ resize: 'vertical' }}
            />
          </div>

          <div className="admin-actions">
            <button
              type="button"
              className="admin-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'جاري الحفظ...' : 'حفظ المقال'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogsSettings;

